/**
 * Phase SC-2b — IA-side accounting (owner finances) API client.
 *
 * Wraps the real-auth tenant-self BE surface added in eduviaos-backend
 * (accounting domain):
 *
 *   Expenses:      GET/POST /api/accounting/expenses, GET/PATCH/DELETE /{id}
 *   Recurring:     GET/POST /api/accounting/recurring-expenses,
 *                  PATCH /{id}, POST /{id}/pause|resume|cancel|skip
 *   Staff payees:  GET/POST /api/accounting/staff-payees, GET/PATCH/DELETE /{id}
 *   P&L report:    GET /api/accounting/reports/pnl?year=&month=
 *
 * Tenant + actor are resolved BE-side from the JWT AuthContext; the
 * vertical is derived server-side from the tenant document. Every route
 * is gated on the `accounting` module + `view_accounting` (reads) /
 * `manage_accounting` (writes) permissions.
 *
 * MONEY: every amount is integer PAISE on the wire (₹1,250.00 → 125000),
 * matching the BE. The UI converts rupees ↔ paise at the input edge only;
 * all state and math stay in paise. Wire shapes mirror
 * `app/domains/accounting/schemas.py` exactly — any field-shape change on
 * the BE must land here too.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ApiError } from "@eduviaos/shared";
import { api } from "@/lib/api";

// ── Closed vocabularies (mirror schemas.py) ────────────────────────────

export type ExpenseCategory =
  | "rent"
  | "electricity"
  | "water"
  | "internet"
  | "maintenance"
  | "supplies"
  | "equipment"
  | "salary"
  | "marketing"
  | "software"
  | "taxes"
  | "professional_fees"
  | "travel"
  | "renovation"
  | "casual_labour"
  | "other";

export type ManualExpenseRecurrence = "one_time";
export type RecurringExpenseCadence = "monthly" | "quarterly" | "annual";
export type RecurringExpenseStatus = "active" | "paused" | "cancelled";
export type RecurringEndMode = "never" | "until" | "count";
/** Ledger rows may be one-time, or a materialized occurrence of a schedule. */
export type ExpenseRecurrence = ManualExpenseRecurrence | RecurringExpenseCadence;

export type PaymentMethod =
  | "cash"
  | "upi"
  | "card"
  | "netbanking"
  | "cheque"
  | "bank_transfer"
  | "other";

export const EXPENSE_CATEGORY_LABELS: Record<ExpenseCategory, string> = {
  rent: "Rent",
  electricity: "Electricity",
  water: "Water",
  internet: "Internet",
  maintenance: "Maintenance",
  supplies: "Supplies",
  equipment: "Equipment",
  salary: "Staff wages",
  marketing: "Marketing",
  software: "Software",
  taxes: "Taxes",
  professional_fees: "Professional fees",
  travel: "Travel",
  renovation: "Renovation",
  casual_labour: "Casual labour",
  other: "Other",
};

export const EXPENSE_RECURRENCE_LABELS: Record<ExpenseRecurrence, string> = {
  one_time: "One-time",
  monthly: "Monthly",
  quarterly: "Quarterly",
  annual: "Annual",
};

export const RECURRING_CADENCE_LABELS: Record<RecurringExpenseCadence, string> = {
  monthly: "Every month",
  quarterly: "Every quarter",
  annual: "Every year",
};

export const RECURRING_STATUS_LABELS: Record<RecurringExpenseStatus, string> = {
  active: "Active",
  paused: "Paused",
  cancelled: "Cancelled",
};

export function expenseCategoryLabel(
  category: string,
  customLabel: string | null | undefined,
): string {
  if (category === "other" && customLabel && customLabel.trim().length > 0) {
    return customLabel.trim();
  }
  return EXPENSE_CATEGORY_LABELS[category as ExpenseCategory] ?? category;
}

export const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = {
  cash: "Cash",
  upi: "UPI",
  card: "Card",
  netbanking: "Net banking",
  cheque: "Cheque",
  bank_transfer: "Bank transfer",
  other: "Other",
};

// ── Wire shapes ────────────────────────────────────────────────────────

export interface Expense {
  id: string;
  tenant_id: string;
  branch_id: string | null;
  title: string;
  category: ExpenseCategory;
  amount_paise: number;
  incurred_on: string;
  recurrence: ExpenseRecurrence;
  recurring_expense_id: string | null;
  occurrence_key: string | null;
  is_manual_override: boolean;
  custom_category_label: string | null;
  vendor_name: string | null;
  payment_method: PaymentMethod | null;
  note: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface ExpenseListResponse {
  items: Expense[];
  total: number;
  total_amount_paise: number;
}

export interface CreateExpenseBody {
  title: string;
  category: ExpenseCategory;
  amount_paise: number;
  incurred_on?: string | null;
  recurrence?: ManualExpenseRecurrence;
  custom_category_label?: string | null;
  vendor_name?: string | null;
  payment_method?: PaymentMethod | null;
  note?: string | null;
  branch_id?: string | null;
}

export type UpdateExpenseBody = Partial<CreateExpenseBody>;

export interface ExpenseListFilters {
  category?: ExpenseCategory;
  incurred_from?: string;
  incurred_to?: string;
  limit?: number;
  offset?: number;
}

export interface StaffPayee {
  id: string;
  tenant_id: string;
  branch_id: string | null;
  name: string;
  role_label: string;
  has_portal_access: boolean;
  linked_user_id: string | null;
  monthly_amount_paise: number;
  payment_method: PaymentMethod | null;
  is_active: boolean;
  note: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface StaffPayeeListResponse {
  items: StaffPayee[];
  total: number;
  active_monthly_cost_paise: number;
}

export interface CreateStaffPayeeBody {
  name: string;
  role_label: string;
  monthly_amount_paise: number;
  has_portal_access?: boolean;
  linked_user_id?: string | null;
  payment_method?: PaymentMethod | null;
  is_active?: boolean;
  note?: string | null;
  branch_id?: string | null;
}

export type UpdateStaffPayeeBody = Partial<CreateStaffPayeeBody>;

export interface StaffPayeeListFilters {
  is_active?: boolean;
  limit?: number;
  offset?: number;
}

export interface ExpenseCategoryTotal {
  category: ExpenseCategory;
  custom_category_label?: string | null;
  amount_paise: number;
}

export interface RecurringExpense {
  id: string;
  tenant_id: string;
  branch_id: string | null;
  title: string;
  category: ExpenseCategory;
  custom_category_label: string | null;
  amount_paise: number;
  cadence: RecurringExpenseCadence;
  starts_on: string;
  ends_on: string | null;
  occurrence_count: number | null;
  status: RecurringExpenseStatus;
  paused_at: string | null;
  skipped_occurrence_keys: string[];
  next_occurrence_on: string | null;
  vendor_name: string | null;
  payment_method: PaymentMethod | null;
  note: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface RecurringExpenseListResponse {
  items: RecurringExpense[];
  total: number;
}

export interface CreateRecurringExpenseBody {
  title: string;
  category: ExpenseCategory;
  custom_category_label?: string | null;
  amount_paise: number;
  cadence: RecurringExpenseCadence;
  starts_on: string;
  ends_on?: string | null;
  occurrence_count?: number | null;
  vendor_name?: string | null;
  payment_method?: PaymentMethod | null;
  note?: string | null;
  branch_id?: string | null;
}

export interface UpdateRecurringExpenseBody extends Partial<CreateRecurringExpenseBody> {
  effective_from: string;
}

export interface RecurringExpenseListFilters {
  status?: RecurringExpenseStatus;
  limit?: number;
  offset?: number;
}

export interface RecurringScheduleDraft {
  title: string;
  category: ExpenseCategory;
  customCategoryLabel: string;
  amountPaise: number;
  cadence: RecurringExpenseCadence;
  startsOn: string;
  endMode: RecurringEndMode;
  endsOn: string;
  occurrenceCount: number;
  vendorName: string | null;
  paymentMethod: PaymentMethod | null;
  note: string | null;
}

/** Encode the three end modes into the mutually exclusive API fields. */
export function buildRecurringExpensePayload(
  input: RecurringScheduleDraft,
): CreateRecurringExpenseBody {
  const custom =
    input.category === "other" && input.customCategoryLabel.trim().length > 0
      ? input.customCategoryLabel.trim()
      : null;
  return {
    title: input.title.trim(),
    category: input.category,
    custom_category_label: custom,
    amount_paise: input.amountPaise,
    cadence: input.cadence,
    starts_on: input.startsOn,
    ends_on: input.endMode === "until" ? input.endsOn : null,
    occurrence_count: input.endMode === "count" ? input.occurrenceCount : null,
    vendor_name: input.vendorName,
    payment_method: input.paymentMethod,
    note: input.note,
  };
}

export interface ProfitLoss {
  year: number;
  month: number;
  period_start: string;
  period_end: string;
  fee_income_paise: number;
  staff_cost_paise: number;
  /**
   * Phase SC-2c: `payroll_run` when a finalized pay run for this month drove
   * the staff-cost line, `recurring` when it fell back to the recurring
   * StaffPayee estimate. Lets the P&L surface explain *why* the number is
   * what it is (and link to the run).
   */
  staff_cost_source: "payroll_run" | "recurring";
  expenses_paise: number;
  one_time_expenses_paise: number;
  scheduled_expenses_paise: number;
  gross_profit_paise: number;
  net_profit_paise: number;
  expense_breakdown: ExpenseCategoryTotal[];
}

// ── Money helpers ──────────────────────────────────────────────────────

const INR = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  maximumFractionDigits: 2,
});

/** Format integer paise as an ₹ INR string. `125000` → `₹1,250.00`. */
export function formatPaise(paise: number): string {
  return INR.format(paise / 100);
}

/** Compact rupee (no paise) — for stat cards. `125000` → `₹1,250`. */
export function formatPaiseCompact(paise: number): string {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(paise / 100);
}

/** Parse a rupee string/number to integer paise. Returns null when invalid. */
export function rupeesToPaise(rupees: string | number): number | null {
  const n = typeof rupees === "number" ? rupees : Number.parseFloat(rupees);
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.round(n * 100);
}

/** Integer paise → rupee number for prefilling inputs. `125000` → `1250`. */
export function paiseToRupees(paise: number): number {
  return paise / 100;
}

// ── React Query hooks ──────────────────────────────────────────────────

const EXPENSES_KEY = ["ia", "accounting", "expenses"] as const;
const RECURRING_KEY = ["ia", "accounting", "recurring-expenses"] as const;
const STAFF_KEY = ["ia", "accounting", "staff-payees"] as const;
const PNL_KEY = ["ia", "accounting", "pnl"] as const;

function toQuery(params: Record<string, string | number | boolean | undefined>): string {
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined) usp.set(k, String(v));
  }
  const s = usp.toString();
  return s ? `?${s}` : "";
}

export function useExpenses(filters: ExpenseListFilters) {
  return useQuery<ExpenseListResponse>({
    queryKey: [...EXPENSES_KEY, filters],
    queryFn: () =>
      api.get<ExpenseListResponse>(`/api/accounting/expenses${toQuery({ ...filters })}`),
    staleTime: 15_000,
  });
}

export function useCreateExpense() {
  const qc = useQueryClient();
  return useMutation<Expense, ApiError, CreateExpenseBody>({
    mutationFn: (body) => api.post<Expense>("/api/accounting/expenses", body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: EXPENSES_KEY });
      void qc.invalidateQueries({ queryKey: PNL_KEY });
    },
  });
}

export function useUpdateExpense() {
  const qc = useQueryClient();
  return useMutation<Expense, ApiError, { id: string; body: UpdateExpenseBody }>({
    mutationFn: ({ id, body }) => api.patch<Expense>(`/api/accounting/expenses/${id}`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: EXPENSES_KEY });
      void qc.invalidateQueries({ queryKey: PNL_KEY });
    },
  });
}

export function useDeleteExpense() {
  const qc = useQueryClient();
  return useMutation<void, ApiError, string>({
    mutationFn: (id) => api.delete<void>(`/api/accounting/expenses/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: EXPENSES_KEY });
      void qc.invalidateQueries({ queryKey: PNL_KEY });
    },
  });
}

export function useStaffPayees(filters: StaffPayeeListFilters) {
  return useQuery<StaffPayeeListResponse>({
    queryKey: [...STAFF_KEY, filters],
    queryFn: () =>
      api.get<StaffPayeeListResponse>(`/api/accounting/staff-payees${toQuery({ ...filters })}`),
    staleTime: 15_000,
  });
}

export function useCreateStaffPayee() {
  const qc = useQueryClient();
  return useMutation<StaffPayee, ApiError, CreateStaffPayeeBody>({
    mutationFn: (body) => api.post<StaffPayee>("/api/accounting/staff-payees", body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: STAFF_KEY });
      void qc.invalidateQueries({ queryKey: PNL_KEY });
    },
  });
}

export function useUpdateStaffPayee() {
  const qc = useQueryClient();
  return useMutation<StaffPayee, ApiError, { id: string; body: UpdateStaffPayeeBody }>({
    mutationFn: ({ id, body }) => api.patch<StaffPayee>(`/api/accounting/staff-payees/${id}`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: STAFF_KEY });
      void qc.invalidateQueries({ queryKey: PNL_KEY });
    },
  });
}

export function useDeleteStaffPayee() {
  const qc = useQueryClient();
  return useMutation<void, ApiError, string>({
    mutationFn: (id) => api.delete<void>(`/api/accounting/staff-payees/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: STAFF_KEY });
      void qc.invalidateQueries({ queryKey: PNL_KEY });
    },
  });
}

export interface ProfitLossSeriesPoint {
  year: number;
  month: number;
  label: string;
  fee_income_paise: number;
  staff_cost_paise: number;
  expenses_paise: number;
  one_time_expenses_paise: number;
  scheduled_expenses_paise: number;
  gross_profit_paise: number;
  net_profit_paise: number;
}

export interface ProfitLossSeries {
  months: ProfitLossSeriesPoint[];
}

export interface ExpenseAssist {
  category: ExpenseCategory;
  custom_category_label: string | null;
  kind: "one_time" | "recurring";
  cadence: RecurringExpenseCadence | null;
  rationale: string;
  provider: string;
  model: string;
  fallback_depth: number;
}

export interface PeriodExplain {
  headline: string;
  paragraphs: string[];
  provider: string;
  model: string;
  fallback_depth: number;
}

export function monthDateBounds(year: number, month: number): { min: string; max: string } {
  const last = new Date(year, month, 0).getDate();
  const mm = String(month).padStart(2, "0");
  return {
    min: `${year}-${mm}-01`,
    max: `${year}-${mm}-${String(last).padStart(2, "0")}`,
  };
}

export function useProfitLoss(year: number, month: number) {
  return useQuery<ProfitLoss>({
    queryKey: [...PNL_KEY, year, month],
    queryFn: () => api.get<ProfitLoss>(`/api/accounting/reports/pnl${toQuery({ year, month })}`),
    staleTime: 15_000,
  });
}

export function useProfitLossSeries(year: number, month: number, months = 6) {
  return useQuery<ProfitLossSeries>({
    queryKey: [...PNL_KEY, "series", year, month, months],
    queryFn: () =>
      api.get<ProfitLossSeries>(
        `/api/accounting/reports/pnl-series${toQuery({ year, month, months })}`,
      ),
    staleTime: 15_000,
  });
}

export function useSuggestExpense() {
  return useMutation<
    ExpenseAssist,
    ApiError,
    { title: string; note?: string; kind: "one_time" | "recurring" }
  >({
    mutationFn: (body) => api.post<ExpenseAssist>("/api/accounting/assist/expense", body),
  });
}

export function useExplainPeriod() {
  const qc = useQueryClient();
  return useMutation<PeriodExplain, ApiError, { year: number; month: number }>({
    mutationFn: (body) => api.post<PeriodExplain>("/api/accounting/assist/explain", body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: PNL_KEY });
    },
  });
}

function invalidateLedger(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: EXPENSES_KEY });
  void qc.invalidateQueries({ queryKey: RECURRING_KEY });
  void qc.invalidateQueries({ queryKey: PNL_KEY });
}

export function useRecurringExpenses(filters: RecurringExpenseListFilters) {
  return useQuery<RecurringExpenseListResponse>({
    queryKey: [...RECURRING_KEY, filters],
    queryFn: () =>
      api.get<RecurringExpenseListResponse>(
        `/api/accounting/recurring-expenses${toQuery({ ...filters })}`,
      ),
    staleTime: 15_000,
  });
}

export function useCreateRecurringExpense() {
  const qc = useQueryClient();
  return useMutation<RecurringExpense, ApiError, CreateRecurringExpenseBody>({
    mutationFn: (body) => api.post<RecurringExpense>("/api/accounting/recurring-expenses", body),
    onSuccess: () => invalidateLedger(qc),
  });
}

export function useUpdateRecurringExpense() {
  const qc = useQueryClient();
  return useMutation<RecurringExpense, ApiError, { id: string; body: UpdateRecurringExpenseBody }>({
    mutationFn: ({ id, body }) =>
      api.patch<RecurringExpense>(`/api/accounting/recurring-expenses/${id}`, body),
    onSuccess: () => invalidateLedger(qc),
  });
}

export function useRecurringExpenseAction() {
  const qc = useQueryClient();
  return useMutation<
    RecurringExpense,
    ApiError,
    { id: string; action: "pause" | "resume" | "cancel" | "skip"; on: string }
  >({
    mutationFn: ({ id, action, on }) => {
      const body = action === "skip" ? { occurrence_on: on } : { effective_on: on };
      return api.post<RecurringExpense>(`/api/accounting/recurring-expenses/${id}/${action}`, body);
    },
    onSuccess: () => invalidateLedger(qc),
  });
}
